// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AccountMessages from "@insite/content-library/Widgets/MyAccount/AccountMessages";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

// XNG-Changes
enum fields {
    showSignOut = "showSignOut",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showSignOut]: boolean;
    };
}

type Props = OwnProps;

export interface MyAccountViewStyles {
    quickLinksGridItem?: GridItemProps;
    mainSectionGridItem?: GridItemProps;
    mainSectionGridContainer?: GridContainerProps;
    accountMessagesGridItem?: GridItemProps;
    dashboardWidgetsGridItem?: GridItemProps;
    // XNG-Changes
    container?: InjectableCss;
    navigationList?: InjectableCss;
    extraCardsDesktop?: HiddenProps;
    extraCardsMobile?: InjectableCss;
    pageTitle?: InjectableCss;
    welcomeSection?: InjectableCss;
    makeDashboardCheckbox?: InjectableCss;
    accountMessages?: InjectableCss;
    quickOrderSection?: InjectableCss;
    dashboardWidgets?: InjectableCss;
    messageWrapper?: InjectableCss;
}

export const myAccountViewStyles: MyAccountViewStyles = {
    quickLinksGridItem: { width: [12, 12, 12, 2, 2] },
    mainSectionGridItem: { width: [12, 12, 12, 10, 10] },
    accountMessagesGridItem: { width: 12 },
    dashboardWidgetsGridItem: { width: 12 },
};

const styles = myAccountViewStyles;

class MyAccountView extends React.PureComponent<Props> {
    render() {
        // XNG-Changes: Update structure
        return (
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.navigationList}>
                    <XngZone contentId={this.props.id} zoneName="NavigationList" hint="Links & Profile" />
                    <Hidden {...styles.extraCardsDesktop}>
                        <XngZone contentId={this.props.id} zoneName="ExtraCards" hint="Extra Cards" />
                    </Hidden>
                </StyledWrapper>

                <StyledWrapper {...styles.pageTitle}>
                    <XngZone contentId={this.props.id} zoneName="PageTitle" hint="Page Title" />
                </StyledWrapper>

                <StyledWrapper {...styles.welcomeSection}>
                    <XngZone contentId={this.props.id} zoneName="WelcomeMessage" hint="Welcome Message" />
                </StyledWrapper>

                <StyledWrapper {...styles.accountMessages}>
                    <AccountMessages />
                </StyledWrapper>

                <StyledWrapper {...styles.quickOrderSection}>
                    <XngZone contentId={this.props.id} zoneName="QuickOrder" hint="Quick Order" />
                </StyledWrapper>

                <StyledWrapper {...styles.dashboardWidgets}>
                    <XngZone contentId={this.props.id} zoneName="DashboardWidgets" hint="Dashboard Widgets" />
                </StyledWrapper>

                <Hidden above="md" {...styles.extraCardsMobile}>
                    <XngZone contentId={this.props.id} zoneName="ExtraCards" hint="Extra Cards" />
                </Hidden>
            </StyledWrapper>
        );
    }
}

const myAccountViewWidget: WidgetModule = {
    component: MyAccountView,
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        fieldDefinitions: [
            {
                name: fields.showSignOut,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default myAccountViewWidget;
